import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { useTranslation } from 'react-i18next';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { flushQueuedWrites, pendingWriteCount } from '../utils/writeOutbox';

// Drains the offline write queue when connectivity returns and keeps the user
// informed about work that is still only stored on this device.
export default function PendingWritesIndicator() {
  const online = useOnlineStatus();
  const { t } = useTranslation();
  const [pending, setPending] = useState(0);
  const [justSent, setJustSent] = useState(0);
  const flushing = useRef(false);

  const refresh = useCallback(async () => {
    setPending(await pendingWriteCount());
  }, []);

  const flush = useCallback(async () => {
    if (flushing.current) return; // one drain at a time; retries are key-safe but pointless in parallel
    flushing.current = true;
    try {
      const { sent } = await flushQueuedWrites();
      if (sent > 0) {
        setJustSent(sent);
        // Anything the queue just wrote is now stale in the cache-backed views.
        try { window.dispatchEvent(new CustomEvent('app:outbox:flushed', { detail: { sent } })); } catch (_) {}
      }
    } finally {
      flushing.current = false;
      refresh();
    }
  }, [refresh]);

  // Drain on reconnect, and once on mount to pick up anything left by a previous
  // session that ended before the queue emptied.
  useEffect(() => {
    refresh();
    if (online) flush();
  }, [online, flush, refresh]);

  // A queued write can happen while already "online" (a request that times out),
  // so keep the count fresh rather than only reacting to connectivity changes.
  useEffect(() => {
    const onAuthChanged = () => refresh();
    window.addEventListener('app:auth:changed', onAuthChanged);
    const id = setInterval(refresh, 30000);
    return () => {
      window.removeEventListener('app:auth:changed', onAuthChanged);
      clearInterval(id);
    };
  }, [refresh]);

  return (
    <>
      <Snackbar
        open={pending > 0}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="info" icon={<CloudSyncIcon fontSize="small" />} variant="filled" sx={{ width: '100%' }}>
          {t('offlineWrites.pending', { count: pending })}
        </Alert>
      </Snackbar>

      <Snackbar
        open={justSent > 0}
        autoHideDuration={4000}
        onClose={() => setJustSent(0)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }} onClose={() => setJustSent(0)}>
          {t('offlineWrites.synced', { count: justSent })}
        </Alert>
      </Snackbar>
    </>
  );
}
